import React, { useState, useEffect } from 'react';
import { Fuel, Plus, Search, AlertTriangle, TrendingUp, DollarSign, Droplet, BarChart3, CheckCircle, XCircle, Clock, Edit, Trash2 } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { formatCurrency } from '../utils/formatters';

const FuelManagementPage = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [fraudAlerts, setFraudAlerts] = useState([]);
  const [stations, setStations] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showStationModal, setShowStationModal] = useState(false);
  const [loadingCNPJ, setLoadingCNPJ] = useState(false);
  const [editingStation, setEditingStation] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [invoicePhoto, setInvoicePhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);

  const [formData, setFormData] = useState({
    vehicle_id: '',
    driver_id: '',
    fuel_station_id: '',
    fuel_type: 'diesel',
    quantity_liters: '',
    price_per_liter: '',
    total_amount: '',
    odometer_reading: '',
    is_full_tank: false,
    payment_method: '',
    voucher_number: '',
    notes: ''
  });

  const [stationFormData, setStationFormData] = useState({
    name: '',
    cnpj: '',
    address: '',
    city: '',
    state: '',
    phone: '',
    contact_person: '',
    has_contract: false,
    contract_discount_percent: '',
    payment_terms: '',
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, [activeTab, filterStatus]);

  useEffect(() => {
    if (showModal) {
      if (vehicles.length === 0) loadVehicles();
      if (paymentMethods.length === 0) loadPaymentMethods();
    }
  }, [showModal]);

  const loadVehicles = async () => {
    try {
      const res = await api.get('/api/vehicles');
      setVehicles(res.data.data || res.data || []);
    } catch (error) {
      console.error('Erro ao carregar veículos:', error);
    }
  };

  const loadPaymentMethods = async () => {
    try {
      const res = await api.get('/api/payment-methods');
      setPaymentMethods(res.data.data || res.data || []);
    } catch (error) {
      console.error('Erro ao carregar formas de pagamento:', error);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);

      if (activeTab === 'dashboard') {
        const dashRes = await api.get('/api/fuel/dashboard');
        setDashboard(dashRes.data.data);
      } else if (activeTab === 'transactions') {
        const params = filterStatus !== 'all' ? { status: filterStatus } : {};
        const transRes = await api.get('/api/fuel/transactions', { params });
        setTransactions(transRes.data.data || []);
      } else if (activeTab === 'fraud') {
        const fraudRes = await api.get('/api/fuel/fraud-alerts', { params: { status: 'pending' } });
        setFraudAlerts(fraudRes.data.data || []);
      } else if (activeTab === 'stations') {
        const stationsRes = await api.get('/api/fuel/stations');
        setStations(stationsRes.data.data || []);
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    console.log('=== DEBUG FORM SUBMISSION ===');
    console.log('formData:', formData);

    // Validação dos campos obrigatórios
    if (!formData.vehicle_id || !formData.fuel_type || !formData.quantity_liters || !formData.price_per_liter || !formData.total_amount) {
      console.log('Validação falhou:', {
        vehicle_id: formData.vehicle_id,
        fuel_type: formData.fuel_type,
        quantity_liters: formData.quantity_liters,
        price_per_liter: formData.price_per_liter,
        total_amount: formData.total_amount
      });
      toast.error('Preencha todos os campos obrigatórios (*)');
      return;
    }

    try {
      // Prepare JSON data (backend expects JSON, not FormData)
      const dataToSend = {
        vehicle_id: formData.vehicle_id,
        fuel_type: formData.fuel_type,
        quantity_liters: parseFloat(formData.quantity_liters),
        price_per_liter: parseFloat(formData.price_per_liter),
        total_amount: parseFloat(formData.total_amount),
        is_full_tank: formData.is_full_tank ? 1 : 0
      };

      // Add optional fields only if they have values
      if (formData.driver_id) dataToSend.driver_id = formData.driver_id;
      if (formData.fuel_station_id) dataToSend.fuel_station_id = formData.fuel_station_id;
      if (formData.odometer_reading) dataToSend.odometer_reading = parseInt(formData.odometer_reading);
      if (formData.payment_method) dataToSend.payment_method = formData.payment_method;
      if (formData.voucher_number) dataToSend.voucher_number = formData.voucher_number;
      if (formData.notes) dataToSend.notes = formData.notes;

      console.log('Dados a enviar:', dataToSend);

      // Note: Photo upload will be implemented later when backend Multer middleware is added
      if (invoicePhoto) {
        toast.info('Upload de foto da nota fiscal será implementado em breve');
      }

      await api.post('/api/fuel/transactions', dataToSend);

      toast.success('Abastecimento registrado com sucesso!');
      setShowModal(false);
      resetForm();
      loadData();
    } catch (error) {
      console.error('Erro ao registrar:', error);
      console.error('Resposta do servidor:', error.response?.data);
      toast.error(error.response?.data?.message || 'Erro ao registrar abastecimento');
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB max
        toast.error('Arquivo muito grande! Máximo 5MB.');
        return;
      }
      setInvoicePhoto(file);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleApprove = async (id) => {
    try {
      await api.put(`/api/fuel/transactions/${id}/status`, {
        status: 'approved',
        approved_by: 'admin' // TODO: usar user atual
      });
      toast.success('Transação aprovada!');
      loadData();
    } catch (error) {
      console.error('Erro ao aprovar:', error);
      toast.error('Erro ao aprovar transação');
    }
  };

  const handleReject = async (id) => {
    try {
      await api.put(`/api/fuel/transactions/${id}/status`, {
        status: 'rejected',
        approved_by: 'admin'
      });
      toast.success('Transação rejeitada!');
      loadData();
    } catch (error) {
      console.error('Erro ao rejeitar:', error);
      toast.error('Erro ao rejeitar transação');
    }
  };

  const resetForm = () => {
    setFormData({
      vehicle_id: '',
      driver_id: '',
      fuel_station_id: '',
      fuel_type: 'diesel',
      quantity_liters: '',
      price_per_liter: '',
      total_amount: '',
      odometer_reading: '',
      is_full_tank: false,
      payment_method: '',
      voucher_number: '',
      notes: ''
    });
    setInvoicePhoto(null);
    setPhotoPreview(null);
  };

  const calculateTotal = () => {
    const qty = parseFloat(formData.quantity_liters) || 0;
    const price = parseFloat(formData.price_per_liter) || 0;
    setFormData({ ...formData, total_amount: (qty * price).toFixed(2) });
  };

  const handleCNPJChange = async (e) => {
    const value = e.target.value;
    const cnpjNumbers = value.replace(/[^\d]/g, '');

    // Formatar CNPJ: 00.000.000/0000-00
    let formattedCNPJ = value;
    if (cnpjNumbers.length <= 14) {
      formattedCNPJ = cnpjNumbers
        .replace(/^(\d{2})(\d)/, '$1.$2')
        .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
        .replace(/\.(\d{3})(\d)/, '.$1/$2')
        .replace(/(\d{4})(\d)/, '$1-$2');
    }

    setStationFormData({ ...stationFormData, cnpj: formattedCNPJ });

    // Buscar automaticamente quando tiver 14 dígitos
    if (cnpjNumbers.length === 14) {
      setLoadingCNPJ(true);

      try {
        const response = await api.get(`/api/receitaws/cnpj/${cnpjNumbers}`);

        if (response.data.success) {
          const data = response.data.data;

          // Preencher automaticamente os campos
          setStationFormData(prev => ({
            ...prev,
            cnpj: formattedCNPJ,
            name: data.nome_fantasia || data.razao_social || prev.name,
            address: data.logradouro
              ? `${data.logradouro}, ${data.numero || 'S/N'}${data.complemento ? ' - ' + data.complemento : ''}, ${data.bairro || ''}`
              : prev.address,
            city: data.municipio || prev.city,
            state: data.uf || prev.state,
            phone: data.telefone || prev.phone
          }));

          toast.success(`✅ CNPJ encontrado: ${data.razao_social || data.nome_fantasia}`, { duration: 4000 });
        }
      } catch (error) {
        console.error('Erro ao buscar CNPJ:', error);

        if (error.response?.status === 404) {
          toast.error('CNPJ não encontrado na Receita Federal');
        } else {
          toast.error('Erro ao consultar CNPJ');
        }
      } finally {
        setLoadingCNPJ(false);
      }
    }
  };

  const handleStationSubmit = async (e) => {
    e.preventDefault();

    if (!stationFormData.name) {
      toast.error('Nome do posto é obrigatório');
      return;
    }

    try {
      const dataToSend = {
        name: stationFormData.name,
        has_contract: stationFormData.has_contract ? 1 : 0
      };

      if (stationFormData.cnpj) dataToSend.cnpj = stationFormData.cnpj;
      if (stationFormData.address) dataToSend.address = stationFormData.address;
      if (stationFormData.city) dataToSend.city = stationFormData.city;
      if (stationFormData.state) dataToSend.state = stationFormData.state;
      if (stationFormData.phone) dataToSend.phone = stationFormData.phone;
      if (stationFormData.contact_person) dataToSend.contact_person = stationFormData.contact_person;
      if (stationFormData.contract_discount_percent) dataToSend.contract_discount_percent = parseFloat(stationFormData.contract_discount_percent);
      if (stationFormData.payment_terms) dataToSend.payment_terms = stationFormData.payment_terms;
      if (stationFormData.notes) dataToSend.notes = stationFormData.notes;

      if (editingStation) {
        // Modo edição
        await api.put(`/api/fuel/stations/${editingStation.id}`, dataToSend);
        toast.success('Posto atualizado com sucesso!');
      } else {
        // Modo criação
        await api.post('/api/fuel/stations', dataToSend);
        toast.success('Posto cadastrado com sucesso!');
      }

      setShowStationModal(false);
      resetStationForm();
      setEditingStation(null);
      loadData();
    } catch (error) {
      console.error('Erro ao salvar posto:', error);
      toast.error(error.response?.data?.message || 'Erro ao salvar posto');
    }
  };

  const handleEditStation = (station) => {
    setEditingStation(station);
    setStationFormData({
      name: station.name || '',
      cnpj: station.cnpj || '',
      address: station.address || '',
      city: station.city || '',
      state: station.state || '',
      phone: station.phone || '',
      contact_person: station.contact_person || '',
      has_contract: station.has_contract === 1,
      contract_discount_percent: station.contract_discount_percent || '',
      payment_terms: station.payment_terms || '',
      notes: station.notes || ''
    });
    setShowStationModal(true);
  };

  const handleDeleteStation = async (station) => {
    if (!window.confirm(`Tem certeza que deseja excluir o posto "${station.name}"?\n\nEsta ação não pode ser desfeita.`)) {
      return;
    }

    try {
      await api.delete(`/api/fuel/stations/${station.id}`);
      toast.success('Posto excluído com sucesso!');
      loadData();
    } catch (error) {
      console.error('Erro ao excluir posto:', error);

      // Mensagem específica se houver transações vinculadas
      if (error.response?.data?.transactionCount) {
        toast.error(`Não é possível excluir. Existem ${error.response.data.transactionCount} transações vinculadas a este posto.`);
      } else {
        toast.error(error.response?.data?.message || 'Erro ao excluir posto');
      }
    }
  };

  const resetStationForm = () => {
    setStationFormData({
      name: '',
      cnpj: '',
      address: '',
      city: '',
      state: '',
      phone: '',
      contact_person: '',
      has_contract: false,
      contract_discount_percent: '',
      payment_terms: '',
      notes: ''
    });
    setEditingStation(null);
  };

  const getStatusColor = (status) => {
    if (status === 'approved') return 'text-green-600 bg-green-100';
    if (status === 'rejected') return 'text-red-600 bg-red-100';
    if (status === 'fraud_alert') return 'text-orange-600 bg-orange-100';
    return 'text-yellow-600 bg-yellow-100';
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'Pendente',
      approved: 'Aprovado',
      rejected: 'Rejeitado',
      fraud_alert: 'Alerta de Fraude'
    };
    return labels[status] || status;
  };

  const getSeverityColor = (severity) => {
    if (severity === 'critical') return 'text-red-600 bg-red-100';
    if (severity === 'high') return 'text-orange-600 bg-orange-100';
    if (severity === 'medium') return 'text-yellow-600 bg-yellow-100';
    return 'text-blue-600 bg-blue-100';
  };

  if (loading && activeTab === 'dashboard') {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Gestão de Combustível
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Controle completo de abastecimentos com detecção de fraude
          </p>
        </div>

        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Registrar Abastecimento
        </button>
      </div>

      {/* Tabs */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab('dashboard')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'dashboard'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <BarChart3 className="w-4 h-4 inline mr-2" />
              Dashboard
            </button>
            <button
              onClick={() => setActiveTab('transactions')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'transactions'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <Droplet className="w-4 h-4 inline mr-2" />
              Transações
            </button>
            <button
              onClick={() => setActiveTab('fraud')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'fraud'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <AlertTriangle className="w-4 h-4 inline mr-2" />
              Alertas de Fraude
            </button>
            <button
              onClick={() => setActiveTab('stations')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'stations'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <Fuel className="w-4 h-4 inline mr-2" />
              Postos
            </button>
          </nav>
        </div>

        {/* Content */}
        <div className="p-6">
          {activeTab === 'dashboard' && dashboard && (
            <div className="space-y-6">
              {/* Stats */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Gasto Total</p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                        R$ {(dashboard.summary?.total_cost || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </p>
                    </div>
                    <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-full">
                      <DollarSign className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Total de Litros</p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                        {(dashboard.summary?.total_liters || 0).toLocaleString('pt-BR', { maximumFractionDigits: 0 })}L
                      </p>
                    </div>
                    <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-full">
                      <Droplet className="w-6 h-6 text-green-600 dark:text-green-400" />
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Pendentes</p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                        {dashboard.summary?.pending_transactions || 0}
                      </p>
                    </div>
                    <div className="p-3 bg-yellow-100 dark:bg-yellow-900/30 rounded-full">
                      <Clock className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Alertas Ativos</p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                        {dashboard.summary?.active_fraud_alerts || 0}
                      </p>
                    </div>
                    <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-full">
                      <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Top Consumers */}
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                  Top 10 Veículos - Maior Consumo
                </h3>
                <div className="space-y-3">
                  {(dashboard.top_consumers || []).map((vehicle, index) => (
                    <div key={vehicle.id} className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                          <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                            {index + 1}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {vehicle.license_plate} - {vehicle.model}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {vehicle.transactions_count} abastecimentos • {parseFloat(vehicle.total_fuel).toFixed(0)}L
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          R$ {vehicle.total_cost.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'transactions' && (
            <div className="space-y-4">
              <div className="flex gap-4 mb-4">
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="all">Todos os Status</option>
                  <option value="pending">Pendentes</option>
                  <option value="approved">Aprovados</option>
                  <option value="rejected">Rejeitados</option>
                  <option value="fraud_alert">Alertas de Fraude</option>
                </select>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                        Data
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                        Veículo
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                        Motorista
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                        Combustível
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                        Quantidade
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                        Valor
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {transactions.map((trans) => (
                      <tr key={trans.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {new Date(trans.transaction_date).toLocaleString('pt-BR')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {trans.license_plate}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {trans.driver_name || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {trans.fuel_type}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {parseFloat(trans.quantity_liters).toFixed(2)}L
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {formatCurrency(parseFloat(trans.total_amount))}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(trans.status)}`}>
                            {getStatusLabel(trans.status)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {trans.status === 'pending' && (
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleApprove(trans.id)}
                                className="text-green-600 hover:text-green-800"
                                title="Aprovar"
                              >
                                <CheckCircle className="w-5 h-5" />
                              </button>
                              <button
                                onClick={() => handleReject(trans.id)}
                                className="text-red-600 hover:text-red-800"
                                title="Rejeitar"
                              >
                                <XCircle className="w-5 h-5" />
                              </button>
                            </div>
                          )}
                          {trans.status === 'fraud_alert' && (
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleApprove(trans.id)}
                                className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 flex items-center gap-1"
                                title="Aprovar mesmo com alerta (falso positivo)"
                              >
                                <CheckCircle className="w-4 h-4" />
                                Aprovar
                              </button>
                              <button
                                onClick={() => handleReject(trans.id)}
                                className="px-3 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700 flex items-center gap-1"
                                title="Confirmar fraude e rejeitar"
                              >
                                <XCircle className="w-4 h-4" />
                                Rejeitar
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {transactions.length === 0 && (
                  <div className="text-center py-12">
                    <p className="text-gray-500 dark:text-gray-400">
                      Nenhuma transação encontrada
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'fraud' && (
            <div className="space-y-4">
              {fraudAlerts.map((alert) => (
                <div
                  key={alert.id}
                  className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getSeverityColor(alert.severity)}`}>
                          {alert.severity.toUpperCase()}
                        </span>
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {new Date(alert.alert_timestamp).toLocaleString('pt-BR')}
                        </span>
                      </div>
                      <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-1">
                        {alert.description}
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Veículo: {alert.license_plate} • Motorista: {alert.driver_name || 'Não informado'}
                      </p>
                    </div>
                    <AlertTriangle className="w-6 h-6 text-orange-500" />
                  </div>
                </div>
              ))}

              {fraudAlerts.length === 0 && (
                <div className="text-center py-12">
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <p className="text-gray-500 dark:text-gray-400">
                    Nenhum alerta de fraude ativo
                  </p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'stations' && (
            <div className="space-y-6">
              {/* Header com botão Novo Posto */}
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-medium text-gray-900 dark:text-white">
                  Postos Cadastrados ({stations.length})
                </h2>
                <button
                  onClick={() => setShowStationModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <Plus className="w-4 h-4" />
                  Novo Posto
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {stations.map((station) => (
                <div
                  key={station.id}
                  className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                        {station.name}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        {station.city} - {station.state}
                      </p>
                    </div>
                    {station.has_contract === 1 && (
                      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                        Contrato {station.contract_discount_percent}% OFF
                      </span>
                    )}
                  </div>
                  <div className="space-y-2 mb-4">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {station.address}
                    </p>
                    {station.phone && (
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        📞 {station.phone}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2 pt-4 border-t border-gray-200 dark:border-gray-600">
                    <button
                      onClick={() => handleEditStation(station)}
                      className="flex-1 px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                    >
                      <Edit className="w-4 h-4" />
                      Editar
                    </button>
                    <button
                      onClick={() => handleDeleteStation(station)}
                      className="flex-1 px-3 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
                    >
                      <Trash2 className="w-4 h-4" />
                      Excluir
                    </button>
                  </div>
                </div>
              ))}

              {stations.length === 0 && (
                <div className="col-span-3 text-center py-12">
                  <p className="text-gray-500 dark:text-gray-400">
                    Nenhum posto cadastrado
                  </p>
                </div>
              )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal de Registro */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
                Registrar Abastecimento
              </h2>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Veículo *
                    </label>
                    <select
                      required
                      value={formData.vehicle_id}
                      onChange={(e) => setFormData({ ...formData, vehicle_id: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="">Selecione um veículo</option>
                      {vehicles.map((vehicle) => (
                        <option key={vehicle.id} value={vehicle.id}>
                          {vehicle.license_plate} - {vehicle.model}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Tipo de Combustível *
                    </label>
                    <select
                      required
                      value={formData.fuel_type}
                      onChange={(e) => setFormData({ ...formData, fuel_type: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="diesel">Diesel</option>
                      <option value="gasoline">Gasolina</option>
                      <option value="ethanol">Etanol</option>
                      <option value="gas">GNV</option>
                      <option value="electric">Elétrico</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Quantidade (Litros) *
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      required
                      value={formData.quantity_liters}
                      onChange={(e) => setFormData({ ...formData, quantity_liters: e.target.value })}
                      onBlur={calculateTotal}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Preço por Litro *
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      required
                      value={formData.price_per_liter}
                      onChange={(e) => setFormData({ ...formData, price_per_liter: e.target.value })}
                      onBlur={calculateTotal}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Valor Total *
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      required
                      value={formData.total_amount}
                      onChange={(e) => setFormData({ ...formData, total_amount: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Hodômetro (km)
                    </label>
                    <input
                      type="number"
                      value={formData.odometer_reading}
                      onChange={(e) => setFormData({ ...formData, odometer_reading: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Forma de Pagamento
                    </label>
                    <select
                      value={formData.payment_method}
                      onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="">Selecione...</option>
                      {paymentMethods.map((pm) => (
                        <option key={pm.id} value={pm.name}>
                          {pm.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Número do Voucher
                    </label>
                    <input
                      type="text"
                      value={formData.voucher_number}
                      onChange={(e) => setFormData({ ...formData, voucher_number: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                </div>

                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.is_full_tank}
                      onChange={(e) => setFormData({ ...formData, is_full_tank: e.target.checked })}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      Tanque Cheio
                    </span>
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Observações
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Foto da Nota Fiscal
                    <span className="text-xs text-gray-500 ml-2">(Recomendado para validação automática)</span>
                  </label>
                  <input
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,application/pdf"
                    onChange={handlePhotoChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                  {photoPreview && (
                    <div className="mt-2">
                      <img
                        src={photoPreview}
                        alt="Preview"
                        className="max-w-full h-40 object-contain border border-gray-300 dark:border-gray-600 rounded"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setInvoicePhoto(null);
                          setPhotoPreview(null);
                        }}
                        className="mt-2 text-sm text-red-600 hover:text-red-700"
                      >
                        Remover foto
                      </button>
                    </div>
                  )}
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Registrar
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Cadastro de Posto */}
      {showStationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                {editingStation ? 'Editar Posto' : 'Cadastrar Novo Posto'}
              </h2>

              <form onSubmit={handleStationSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* CNPJ */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      CNPJ {loadingCNPJ && <span className="text-blue-600 text-xs">(buscando...)</span>}
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={stationFormData.cnpj}
                        onChange={handleCNPJChange}
                        className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        placeholder="00.000.000/0000-00"
                        maxLength="18"
                      />
                      {loadingCNPJ && (
                        <Search className="absolute right-3 top-2.5 w-5 h-5 text-blue-600 animate-pulse" />
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Ao digitar 14 números, preenche automaticamente</p>
                  </div>

                  {/* Nome do Posto */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Nome do Posto *
                    </label>
                    <input
                      type="text"
                      value={stationFormData.name}
                      onChange={(e) => setStationFormData({ ...stationFormData, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="Ex: Posto Shell Centro"
                      required
                    />
                  </div>

                  {/* Telefone */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Telefone
                    </label>
                    <input
                      type="text"
                      value={stationFormData.phone}
                      onChange={(e) => setStationFormData({ ...stationFormData, phone: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="(00) 0000-0000"
                    />
                  </div>

                  {/* Endereço */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Endereço
                    </label>
                    <input
                      type="text"
                      value={stationFormData.address}
                      onChange={(e) => setStationFormData({ ...stationFormData, address: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="Rua, número, bairro"
                    />
                  </div>

                  {/* Cidade */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Cidade
                    </label>
                    <input
                      type="text"
                      value={stationFormData.city}
                      onChange={(e) => setStationFormData({ ...stationFormData, city: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="São Paulo"
                    />
                  </div>

                  {/* Estado */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Estado
                    </label>
                    <input
                      type="text"
                      value={stationFormData.state}
                      onChange={(e) => setStationFormData({ ...stationFormData, state: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="SP"
                      maxLength="2"
                    />
                  </div>

                  {/* Pessoa de Contato */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Pessoa de Contato
                    </label>
                    <input
                      type="text"
                      value={stationFormData.contact_person}
                      onChange={(e) => setStationFormData({ ...stationFormData, contact_person: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="Nome do responsável"
                    />
                  </div>

                  {/* Tem Contrato */}
                  <div className="md:col-span-2">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={stationFormData.has_contract}
                        onChange={(e) => setStationFormData({ ...stationFormData, has_contract: e.target.checked })}
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded"
                      />
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Possui contrato de desconto
                      </span>
                    </label>
                  </div>

                  {/* Desconto (se tem contrato) */}
                  {stationFormData.has_contract && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Desconto (%)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={stationFormData.contract_discount_percent}
                        onChange={(e) => setStationFormData({ ...stationFormData, contract_discount_percent: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        placeholder="5.00"
                      />
                    </div>
                  )}

                  {/* Condições de Pagamento (se tem contrato) */}
                  {stationFormData.has_contract && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Condições de Pagamento
                      </label>
                      <input
                        type="text"
                        value={stationFormData.payment_terms}
                        onChange={(e) => setStationFormData({ ...stationFormData, payment_terms: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        placeholder="Ex: 30 dias"
                      />
                    </div>
                  )}

                  {/* Observações */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Observações
                    </label>
                    <textarea
                      value={stationFormData.notes}
                      onChange={(e) => setStationFormData({ ...stationFormData, notes: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      rows="3"
                      placeholder="Informações adicionais..."
                    />
                  </div>
                </div>

                {/* Botões */}
                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowStationModal(false);
                      resetStationForm();
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    {editingStation ? 'Atualizar' : 'Cadastrar Posto'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FuelManagementPage;